import * as Yup from 'yup';

import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { fetchFormFields, postFormData } from '../../../../modules/api';
import { useRecaptchaToken } from '../../../globalHooks/useRecaptchaToken';
import useParams from '../../../globalHooks/useParams';

const useContactForm = () => {
  const params = useParams();

  const [fields, setFields] = useState({});

  const [waiting, setWaiting] = useState(true);

  const [submitInfo, setSubmitInfo] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [initialValues, setInitialValues] = useState({
    tour: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    purpose: null,
    how_hear: null,
    message: '',
    opt_in: false,
    honeypot: null,
    current_page: window.location.href,
    referrer: document.referrer,
    client_id: 'No client id detected',
  });

  const [cookies] = useCookies(['_ga']);
  const executeRecaptcha = useRecaptchaToken();

  // Get _ga client id
  const getGaClientId = () => {
    const gaCookie = cookies._ga;

    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length === 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }

    return 'No client id detected';
  };

  // Set client id when _ga cookie is available
  useEffect(() => {
    const gaClientId = getGaClientId();

    setInitialValues((prevValues) => ({
      ...prevValues,
      client_id: gaClientId,
    }));
  }, [cookies._ga]);

  // Get fields on load
  useEffect(() => {
    (async () => {
      const data = await fetchFormFields('contact', params?.hol);

      setFields(data);
      setSubmitInfo(data.thank_you);
      setInitialValues((prevValues) => ({
        ...prevValues,
        tour: data?.tour?.display,
      }));

      setWaiting(false);
    })();
  }, []);

  const validationSchema = Yup.object({
    first_name: Yup.string().required('* required'),
    last_name: Yup.string().required('* required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
    phone: Yup.string()
      .required('* required')
      .matches(/^\+?\s*\d+(\s+\d+)*\s*$/, 'Please enter a valid phone number'),
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return null;
    }

    const token = await executeRecaptcha('contact');

    return token;
  }, [executeRecaptcha]);

  const postForm = async (data, recaptchaToken, callback) => {
    if (!recaptchaToken || recaptchaToken.trim().length < 1) {
      alert('Please fill out the Recaptcha');
    } else {
      setWaiting(true);

      try {
        const extraFields = {
          form_reference: 'contact',
          id: 'contact-form',
          tour: fields?.tour?.id,
        };

        await postFormData({ ...data, ...extraFields }, recaptchaToken);

        if (callback) callback();

        if (submitInfo.type === 'page') {
          window.location.href = submitInfo.page;
        } else {
          setShowSuccessDialog(true);
        }
      } catch (error) {
        console.error(error);

        if (error.response?.data?.message?.includes('recaptcha')) {
          alert('Invalid recaptcha response');
        }
      }

      setWaiting(false);
    }
  };

  const disable = (errors, values, initialValues) => {
    const hasErrors = Object.keys(errors).length > 0;

    return (
      hasErrors || JSON.stringify(values) === JSON.stringify(initialValues)
    );
  };

  return {
    validationSchema,
    initialValues,
    disable,
    postForm,
    fields,
    submitInfo,
    waiting,
    showSuccessDialog,
    setShowSuccessDialog,
    handleReCaptchaVerify,
  };
};

export default useContactForm;
